import { z } from 'zod';
import { PointSchema } from './types';
// =============================================================================
// GAMESTATE GOVERNOR - COMMAND TYPES
// =============================================================================
/**
 * All command types that can be submitted to the Governor
 * Grouped by domain for clarity
 */
export const CommandTypeSchema = z.enum([
    // Token commands
    'TOKEN_CREATE',
    'TOKEN_MOVE',
    'TOKEN_UPDATE',
    'TOKEN_DELETE',
    'TOKEN_SELECT',
    'TOKEN_DESELECT',
    // Combat commands
    'COMBAT_START',
    'COMBAT_END',
    'COMBAT_NEXT_TURN',
    'COMBAT_PREV_TURN',
    'COMBAT_SET_INITIATIVE',
    'COMBAT_CLEAR_INITIATIVE',
    // Damage & Healing
    'APPLY_DAMAGE',
    'APPLY_HEALING',
    'APPLY_TEMP_HP',
    // Conditions
    'APPLY_CONDITION',
    'REMOVE_CONDITION',
    'TICK_CONDITIONS',
    // Fog of War & Lighting
    'FOG_REVEAL',
    'FOG_HIDE',
    'FOG_RESET',
    'LIGHT_ADD',
    'LIGHT_REMOVE',
    'LIGHT_UPDATE',
    'LIGHT_TOGGLE',
    // Mob management
    'MOB_SPAWN',
    'MOB_REMOVE',
    'MOB_UPDATE',
    // Chat & Dice
    'CHAT_MESSAGE',
    'DICE_ROLL',
    // Scene management
    'SCENE_CHANGE',
    'SCENE_UPDATE',
]);
// =============================================================================
// ACTOR LANES (Who can submit what)
// =============================================================================
/**
 * Actor lanes define permission boundaries
 * Each lane has different allowed commands and restrictions
 */
export const ActorLaneSchema = z.enum([
    'GM_UI', // DM can do anything
    'PLAYER_UI', // Limited to own tokens, chat, dice
    'MOB_DIRECTOR', // AI mob control (move mobs, apply damage)
    'STORY_DIRECTOR', // AI narrative (chat only)
    'RULES_ENGINE', // Automated rules (conditions, turn advance)
    'SYSTEM', // Internal system operations
]);
// =============================================================================
// GOVERNANCE MODES
// =============================================================================
/**
 * Governance modes control how strictly commands are validated
 * - AUDIT: Log violations but apply anyway (development)
 * - SOFT: Apply but auto-correct and warn (transition)
 * - HARD: Reject invalid commands with reason codes (production)
 */
export const GovernanceModeSchema = z.enum(['AUDIT', 'SOFT', 'HARD']);
// =============================================================================
// GAME COMMAND (The core envelope)
// =============================================================================
/**
 * Base game command - the intent submitted to the Governor
 */
export const GameCommandSchema = z.object({
    id: z.string().uuid(),
    type: CommandTypeSchema,
    actor: ActorLaneSchema,
    actorId: z.string(), // userId or "ai:mob-director"
    sceneId: z.string().uuid(),
    timestamp: z.date(),
    payload: z.record(z.unknown()),
    expectedSeq: z.number().int().optional(), // For optimistic concurrency
});
// =============================================================================
// COMMAND PAYLOADS (Type-safe payloads for each command)
// =============================================================================
/**
 * TOKEN_CREATE payload
 */
export const TokenCreatePayloadSchema = z.object({
    name: z.string(),
    position: PointSchema,
    ownerId: z.string().optional(),
    combatantId: z.string().optional(),
    combatantType: z.enum(['character', 'mob']).optional(),
    visuals: z.object({
        shape: z.enum(['circle', 'square']).optional(),
        size: z.number().optional(),
        color: z.string().optional(),
        imageUrl: z.string().optional(),
    }).optional(),
});
/**
 * TOKEN_MOVE payload
 */
export const TokenMovePayloadSchema = z.object({
    tokenId: z.string(),
    to: PointSchema,
    snapToGrid: z.boolean().default(true),
});
/**
 * TOKEN_UPDATE payload
 */
export const TokenUpdatePayloadSchema = z.object({
    tokenId: z.string(),
    updates: z.record(z.unknown()),
});
/**
 * TOKEN_DELETE payload
 */
export const TokenDeletePayloadSchema = z.object({
    tokenId: z.string(),
});
/**
 * APPLY_DAMAGE payload
 */
export const ApplyDamagePayloadSchema = z.object({
    targetId: z.string(),
    targetType: z.enum(['character', 'mob']),
    amount: z.number().int().positive(),
    damageType: z.string().optional(), // "fire", "slashing", etc.
    attackerId: z.string().optional(), // For attention tracking
    source: z.string().optional(), // "Longsword", "Fireball"
});
/**
 * APPLY_HEALING payload
 */
export const ApplyHealingPayloadSchema = z.object({
    targetId: z.string(),
    targetType: z.enum(['character', 'mob']),
    amount: z.number().int().positive(),
    healerId: z.string().optional(),
    source: z.string().optional(),
});
/**
 * APPLY_CONDITION payload
 */
export const ApplyConditionPayloadSchema = z.object({
    targetId: z.string(),
    targetType: z.enum(['character', 'mob']),
    condition: z.string(),
    duration: z.number().int().nullable().optional(), // Rounds, null = indefinite
    source: z.string().optional(),
});
/**
 * REMOVE_CONDITION payload
 */
export const RemoveConditionPayloadSchema = z.object({
    targetId: z.string(),
    targetType: z.enum(['character', 'mob']),
    condition: z.string(),
});
/**
 * COMBAT_START payload
 */
export const CombatStartPayloadSchema = z.object({
    participantIds: z.array(z.string()).optional(),
});
/**
 * COMBAT_SET_INITIATIVE payload
 */
export const CombatSetInitiativePayloadSchema = z.object({
    combatantId: z.string(),
    combatantType: z.enum(['character', 'mob']),
    initiative: z.number(),
    dexMod: z.number().optional(),
});
/**
 * MOB_SPAWN payload
 */
export const MobSpawnPayloadSchema = z.object({
    templateId: z.string().optional(), // SRD monster ID
    name: z.string(),
    position: PointSchema,
    stats: z.object({
        maxHp: z.number().int().positive(),
        ac: z.number().int().positive(),
        cr: z.string().optional(),
    }).optional(),
});
/**
 * FOG_REVEAL payload
 */
export const FogRevealPayloadSchema = z.object({
    cells: z.array(z.object({ x: z.number(), y: z.number() })),
    radius: z.number().optional(), // Reveal radius from center
});
/**
 * LIGHT_ADD payload
 */
export const LightAddPayloadSchema = z.object({
    preset: z.string().optional(), // "torch", "lantern", etc.
    position: PointSchema,
    attachedToTokenId: z.string().optional(),
    brightRadius: z.number().optional(),
    dimRadius: z.number().optional(),
    color: z.string().optional(),
});
/**
 * CHAT_MESSAGE payload
 */
export const ChatMessagePayloadSchema = z.object({
    content: z.string(),
    messageType: z.enum(['text', 'dm', 'system', 'whisper']).default('text'),
    recipientId: z.string().optional(), // For whispers
});
/**
 * DICE_ROLL payload
 */
export const DiceRollPayloadSchema = z.object({
    notation: z.string(), // "1d20+5", "2d6+3"
    label: z.string().optional(), // "attack", "damage", "initiative"
    targetId: z.string().optional(), // Who the roll is against
    isPublic: z.boolean().default(true),
});
// =============================================================================
// COMMAND RESULT
// =============================================================================
/**
 * Result status from command processing
 */
export const CommandResultStatusSchema = z.enum([
    'applied', // Command was applied successfully
    'rejected', // Command was rejected (HARD mode)
    'autocorrected', // Command was modified and applied (SOFT mode)
    'logged', // Command was logged but rejected (AUDIT mode)
]);
/**
 * Rejection reason codes
 */
export const RejectionReasonSchema = z.enum([
    'PERMISSION_DENIED', // Actor doesn't have permission
    'NOT_TOKEN_OWNER', // Player tried to move someone else's token
    'TARGET_NOT_FOUND', // Target entity doesn't exist
    'INVALID_POSITION', // Position out of bounds or occupied
    'COMBAT_NOT_ACTIVE', // Combat command outside combat
    'NOT_YOUR_TURN', // Action out of turn order
    'INSUFFICIENT_RESOURCES', // Not enough spell slots, etc.
    'INVALID_PAYLOAD', // Payload doesn't match schema
    'SEQUENCE_MISMATCH', // expectedSeq doesn't match current
    'BUDGET_EXCEEDED', // AI actor exceeded action budget
]);
/**
 * Result from Governor.submit()
 */
export const CommandResultSchema = z.object({
    status: CommandResultStatusSchema,
    seq: z.number().int().optional(), // Assigned sequence number
    reason: RejectionReasonSchema.optional(),
    message: z.string().optional(),
    corrections: z.record(z.unknown()).optional(), // What was auto-corrected
    resultPayload: z.record(z.unknown()).optional(), // Computed results
});
// =============================================================================
// GAME EVENT (Persisted in game_events table)
// =============================================================================
/**
 * A committed game event (stored in machina_game_events)
 */
export const GameEventSchema = z.object({
    id: z.string().uuid(),
    sceneId: z.string().uuid(),
    seq: z.number().int(),
    ts: z.date(),
    actorLane: ActorLaneSchema,
    actorId: z.string(),
    commandType: CommandTypeSchema,
    commandPayload: z.record(z.unknown()),
    resultStatus: CommandResultStatusSchema,
    resultPayload: z.record(z.unknown()).optional(),
    rejectedReason: RejectionReasonSchema.optional(),
});
// =============================================================================
// PERMISSION MATRIX
// =============================================================================
/**
 * Default permissions for each actor lane
 * GM_UI can do everything, others are restricted
 */
export const DEFAULT_PERMISSIONS = {
    GM_UI: new Set([
        // GM can do everything
        'TOKEN_CREATE', 'TOKEN_MOVE', 'TOKEN_UPDATE', 'TOKEN_DELETE',
        'TOKEN_SELECT', 'TOKEN_DESELECT',
        'COMBAT_START', 'COMBAT_END', 'COMBAT_NEXT_TURN', 'COMBAT_PREV_TURN',
        'COMBAT_SET_INITIATIVE', 'COMBAT_CLEAR_INITIATIVE',
        'APPLY_DAMAGE', 'APPLY_HEALING', 'APPLY_TEMP_HP',
        'APPLY_CONDITION', 'REMOVE_CONDITION', 'TICK_CONDITIONS',
        'FOG_REVEAL', 'FOG_HIDE', 'FOG_RESET',
        'LIGHT_ADD', 'LIGHT_REMOVE', 'LIGHT_UPDATE', 'LIGHT_TOGGLE',
        'MOB_SPAWN', 'MOB_REMOVE', 'MOB_UPDATE',
        'CHAT_MESSAGE', 'DICE_ROLL',
        'SCENE_CHANGE', 'SCENE_UPDATE',
    ]),
    PLAYER_UI: new Set([
        // Players: own tokens + chat + dice
        'TOKEN_MOVE', // Validated to own tokens only
        'TOKEN_SELECT',
        'TOKEN_DESELECT',
        'COMBAT_SET_INITIATIVE', // Own character only
        'CHAT_MESSAGE',
        'DICE_ROLL',
    ]),
    MOB_DIRECTOR: new Set([
        // AI mob control
        'TOKEN_MOVE', // Mob tokens only
        'APPLY_DAMAGE', // Mob attacks
        'CHAT_MESSAGE', // Barks
    ]),
    STORY_DIRECTOR: new Set([
        // AI narrative only
        'CHAT_MESSAGE',
    ]),
    RULES_ENGINE: new Set([
        // Automated rules
        'APPLY_CONDITION',
        'REMOVE_CONDITION',
        'TICK_CONDITIONS',
        'COMBAT_NEXT_TURN', // Auto-advance on timeout
    ]),
    SYSTEM: new Set([
        // Internal operations (all commands for migrations, etc.)
        'TOKEN_CREATE', 'TOKEN_MOVE', 'TOKEN_UPDATE', 'TOKEN_DELETE',
        'TOKEN_SELECT', 'TOKEN_DESELECT',
        'COMBAT_START', 'COMBAT_END', 'COMBAT_NEXT_TURN', 'COMBAT_PREV_TURN',
        'COMBAT_SET_INITIATIVE', 'COMBAT_CLEAR_INITIATIVE',
        'APPLY_DAMAGE', 'APPLY_HEALING', 'APPLY_TEMP_HP',
        'APPLY_CONDITION', 'REMOVE_CONDITION', 'TICK_CONDITIONS',
        'FOG_REVEAL', 'FOG_HIDE', 'FOG_RESET',
        'LIGHT_ADD', 'LIGHT_REMOVE', 'LIGHT_UPDATE', 'LIGHT_TOGGLE',
        'MOB_SPAWN', 'MOB_REMOVE', 'MOB_UPDATE',
        'CHAT_MESSAGE', 'DICE_ROLL',
        'SCENE_CHANGE', 'SCENE_UPDATE',
    ]),
};
/**
 * Check if an actor lane has permission for a command type
 */
export function hasPermission(actor, commandType) {
    return DEFAULT_PERMISSIONS[actor].has(commandType);
}
// =============================================================================
// GOVERNOR CONFIG
// =============================================================================
/**
 * Governor configuration
 */
export const GovernorConfigSchema = z.object({
    mode: GovernanceModeSchema.default('AUDIT'),
    permissions: z.record(ActorLaneSchema, z.array(CommandTypeSchema)).optional(),
    aiBudgets: z.object({
        maxActionsPerRound: z.number().int().positive().default(10),
        maxDamagePerRound: z.number().int().positive().default(200),
    }).optional(),
});
/**
 * Default governor config - starts in AUDIT mode
 */
export const DEFAULT_GOVERNOR_CONFIG = {
    mode: 'AUDIT',
    aiBudgets: {
        maxActionsPerRound: 10,
        maxDamagePerRound: 200,
    },
};
//# sourceMappingURL=commands.js.map